import type { StorefrontOnlineUserModel } from './storefront-activity.dto';

/**
 * PD-17191 — the Activity filter on the Ecommerce Users list. One value at a
 * time; the crm-api list query applies it on the server so the totals and
 * the pages agree.
 *
 * - `online_now`: on a storefront in the last 2 minutes (the Online now set).
 * - `active_7d` / `active_30d`: a storefront visit seen in that many days.
 * - `inactive_60d`: has storefront visits, but none in the last 60 days.
 *   Users with no visits at all are not "inactive"; they never started.
 * - `never_logged_in`: no successful sign-in in the login history.
 */
export const ECOMMERCE_USER_ACTIVITY_FILTER = {
	ANY: 'any',
	ONLINE_NOW: 'online_now',
	ACTIVE_7D: 'active_7d',
	ACTIVE_30D: 'active_30d',
	INACTIVE_60D: 'inactive_60d',
	NEVER_LOGGED_IN: 'never_logged_in',
} as const;

export type EcommerceUserActivityFilterValue =
	(typeof ECOMMERCE_USER_ACTIVITY_FILTER)[keyof typeof ECOMMERCE_USER_ACTIVITY_FILTER];

/** The filter's options, in the order the sidebar lists them. */
export const ECOMMERCE_USER_ACTIVITY_FILTER_OPTIONS: Array<{
	id: EcommerceUserActivityFilterValue;
	label: string;
}> = [
	{ id: ECOMMERCE_USER_ACTIVITY_FILTER.ANY, label: 'Any' },
	{ id: ECOMMERCE_USER_ACTIVITY_FILTER.ONLINE_NOW, label: 'Online now' },
	{ id: ECOMMERCE_USER_ACTIVITY_FILTER.ACTIVE_7D, label: 'Active in 7 days' },
	{ id: ECOMMERCE_USER_ACTIVITY_FILTER.ACTIVE_30D, label: 'Active in 30 days' },
	{
		id: ECOMMERCE_USER_ACTIVITY_FILTER.INACTIVE_60D,
		label: 'Inactive 60+ days',
	},
	{
		id: ECOMMERCE_USER_ACTIVITY_FILTER.NEVER_LOGGED_IN,
		label: 'Never logged in',
	},
];

/**
 * PD-17191 — the storefront activity fields each Ecommerce Users list row
 * carries, for the Last active and Last login columns.
 */
export interface EcommerceUserListActivityFieldsModel {
	/** Latest storefront visit activity (MAX storefront_visit.lastSeenDate), or null with none. */
	lastActiveDate: string | null;
	/** On a storefront right now (the caller sees only accounts they can open). */
	isOnlineNow: boolean;
	/** The Online now entries for this user, for the badge label. */
	storefrontOnline: Array<StorefrontOnlineUserModel>;
	/** More than one active storefront: Online now labels then name the storefront. */
	multipleStorefronts: boolean;
	/** Latest successful sign-in, or null when the user has never signed in. */
	lastLoginDate: string | null;
	/** True when the login history could not be read, so lastLoginDate is unknown rather than "never". */
	lastLoginUnavailable: boolean;
}
